<template>
<b-card
  tag="article"
  class="map-detail-card"
  body-class="map-detail-card-body"
>
  <b-row style="height: 100%" v-if="!isHidden">
    <b-col cols="12">
      <h5 class="map-detail-title">
        <!--Infra icons in separate component displayed on infra popup in map-->
        <infra-icons :infraAT="infraDetails.asset_type"></infra-icons>
        <strong>{{ infraDetails.name }}</strong>
        <!--Infra status information from Nereus-->
        <infra-indicator
          :infraRD="infraDetails.reference_designator"
        ></infra-indicator>
        <b-badge
          id="discontinued-badge"
          v-if="infraDetails.discontinued"
          pill
          variant="secondary"
        >Discontinued</b-badge>
      </h5>
    </b-col>
    <b-col cols="12" class="h-100 w-100">
      <b-tabs
        content-class="map-detail-content"
        nav-wrapper-class="map-detail-nav"
        class="h-100"
      >
        <!--Code for side tab of instruments within infrastructure-->
        <b-tab title="Instruments" active class="h-100 w-100">
          <b-list-group
            v-if="instrumentsList.length > 0"
            class="map-detail-instruments-list"
          >
            <b-list-group-item
              v-for="inst in instrumentsList"
              :key="inst.reference_designator"
              class="flex-column align-items-start instrument-item"
            >
              <div>
                <div class="d-flex w-100 justify-content-between">
                  <h5 class="instrument-title">
                    <!-- Substituting icon img for status indicator -->
                    <!--Inst icons in separate component-->
                    <inst-icons :instAT="inst.asset_type"></inst-icons>
                    <strong style="vertical-align: middle">{{
                      inst.instrument_name
                    }}</strong>
                    <!--Status indicator icon using nereus information-->
                    <status-indicator
                      :instRD="inst.reference_designator"
                    ></status-indicator>
                    <!--Blue cloud to indicate: cloud data is available-->
                    <i
                      :id="`${inst.reference_designator}--cloud-info-available`"
                      v-if="inst.harvest_flag === true"
                      class="fas fa-cloud fa-primary"
                    >
                      <b-popover
                        :target="`${inst.reference_designator}--cloud-info-available`"
                        triggers="hover"
                        placement="bottom"
                      >
                        Cloud data available.
                      </b-popover>
                    </i>
                    <!--Grey cloud to indicate: no cloud data available-->
                    <i
                      :id="`${inst.reference_designator}--cloud-info-unavail`"
                      v-if="inst.harvest_flag === false"
                      class="fas fa-cloud text-muted"
                    >
                      <b-popover
                        :target="`${inst.reference_designator}--cloud-info-unavail`"
                        triggers="hover"
                        placement="bottom"
                        no-fade
                      >
                        Currently not in Interactive Oceans systems. <br>
                        <a
                          :href="`https://ooinet.oceanobservatories.org/data_access/?search=${inst.reference_designator}`"
                          target="_blank"
                        >Click here for official OOI System.</a>
                      </b-popover>
                    </i>
                    <!--Streaming icons (green/red depending if that instrument is streaming
                    from Interactive Oceans system or not.-->
                    <i
                      :id="`${inst.reference_designator}--streaming-from-OOI`"
                      v-if="!isActiveStreams(inst.reference_designator)"
                      class="fas fa-minus-circle fa-danger"
                    >
                      <b-popover
                        :target="`${inst.reference_designator}--streaming-from-OOI`"
                        triggers="hover"
                        placement="bottom"
                        no-fade
                      >
                        Not streaming from Interactive Oceans system.
                      </b-popover>
                    </i>
                    <i
                      :id="`${inst.reference_designator}--streaming-from-IO`"
                      v-if="isActiveStreams(inst.reference_designator)"
                      class="fas fa-rss fa-success"
                    >
                      <b-popover
                        :target="`${inst.reference_designator}--streaming-from-IO`"
                        triggers="hover"
                        placement="bottom"
                        no-fade
                      >
                        Streaming from Interactive Oceans system.
                      </b-popover>
                    </i>
                  </h5>
                </div>
                <div>
                  <b-badge
                    id="discontinued-badge"
                    v-if="inst.discontinued"
                    pill
                    variant="secondary"
                  >Discontinued</b-badge>
                </div>
                <small>{{ inst.reference_designator }}</small>
                <div>
                  <!--Creating a button group so they stay responsive
                  for different screen sizes-->
                  <b-button-group>
                    <b-button
                      variant="outline-info"
                      size="sm"
                      @click="showDetailsModal(inst.reference_designator)"
                      >Details</b-button
                    >
                    <b-button
                      variant="outline-info"
                      size="sm"
                      :href="`${ooiURL}${inst.instrument_url}`"
                      target="_blank"
                    >
                      Learn More
                      <i class="fas fa-external-link-alt fa-sm"></i>
                    </b-button>
                  </b-button-group>
                </div>
              </div>
              <!--No data products available-->
              <h6
                v-if="
                  extractParameters(inst.reference_designator) === null
                "
                class="mt-3"
              >
                No parameters are available.
              </h6>
              <!--Data products available-->
              <h6
                v-if="
                  extractParameters(inst.reference_designator) !== null
                "
                class="mt-3"
              >
                Available parameters
              </h6>
              <!--
                If there is over 1 parameter listed and the time difference
                is not return null, display the count. It won't display the count
                if there's no parameters listed or the time difference is null.
              -->
              <!-- TODO: 5/9/2022 Rollout in the future. Hold back at the moment. -->
              <!-- <div
                v-if="
                  extractParameters(inst.reference_designator) !== null &&
                  RTTimeDiff[inst.reference_designator] !== null
                "
              >
                Real-time data last updated:
                <br>
                TODO: add functionality to change color of badge based
                on time since last updated
                <b-badge :variant="badgeColor">
                  {{ RTTimeDiff[inst.reference_designator] }}
                </b-badge>
              </div> -->
              <b-list-group
                class="mt-3"
                v-if="extractParameters(inst.reference_designator) !== null"
              >
                <b-list-group-item
                  v-for="param in extractParameters(
                    inst.reference_designator
                  )"
                  :key="param.reference_designator + '-' + param.stream"
                  class="flex-column align-items-start"
                >
                  {{ param.parameter_name }}
                  <!-- TODO: Add badge of realtime data back for each parameter -->
                  <!--Badge component that displays realtime data-->
                  <!-- <badge-RT
                    :RTInfo="RTData[inst.reference_designator]"
                    :instStream="inst.preferred_stream"
                    :paramRD="param.reference_designator"
                  ></badge-RT> -->
                  <!--TODO: work on QAQC button-->
                  <!-- <b-button-group size="sm">
                    <b-btn
                      variant="link"
                      pill
                    >
                      <badge-RT
                        :RTInfo="RTData[inst.reference_designator]"
                        :instStream="inst.preferred_stream"
                        :paramRD="param.reference_designator"
                      ></badge-RT>
                    </b-btn>
                    <b-btn
                      variant="link"
                      pill
                    >
                      <qaqc-btn
                        :instRD="inst.reference_designator"
                        :paramRD="param.reference_designator"
                        :paramName="param.parameter_name"
                      ></qaqc-btn>
                    </b-btn>
                  </b-button-group> -->
                </b-list-group-item>
              </b-list-group>
            </b-list-group-item>
          </b-list-group>
          <p v-if="instrumentsList.length === 0">
            No instruments found at this infrastructure.
          </p>
        </b-tab>
        <b-tab title="Details" class="h-100 w-100">
          <div class="map-detail-instruments-list">
            <!-- Infrastructure detail component -->
            <infrastructure-details
              :infrastructure="infraDetails"
              :wpContent="wpContentSelectedInfra"
              :site="siteDetails"
            ></infrastructure-details>
          </div>
        </b-tab>
        <b-tab title="Gallery" class="h-100 w-100">
          <!-- Research area gallery component -->
          <area-media :site="siteDetails"></area-media>
        </b-tab>
      </b-tabs>
    </b-col>
  </b-row>
  <b-row style="height: 100%" v-if="isHidden">
    <b-row class="pb-3">
      <b-col cols="12">
        <b-button
          size="sm"
          variant="link"
          v-on:click="isHidden = !isHidden"
        >
          <i class="far fa-arrow-alt-circle-left"></i>
          Back
        </b-button>
      </b-col>
    </b-row>
    <!--Selected Instrument Details page-->
    <div class="map-detail-instruments-list">
      <!--Overlay with three growing dots to indicate loading-->
      <b-overlay
        :show="loadPage"
        opacity="0.6"
      >
        <template #overlay>
          <div class="d-flex align-items-center">
            <b-spinner small type="grow" variant="secondary"></b-spinner>
            <b-spinner type="grow" variant="dark"></b-spinner>
            <b-spinner small type="grow" variant="secondary"></b-spinner>
            <!-- We add an SR only text for screen readers -->
            <span class="sr-only">Please wait...</span>
          </div>
        </template>
        <b-container fluid>
          <b-row
            v-for="inst in instrumentCard"
            :key="inst.reference_designator"
          >
            <b-col cols="12">
              <h5>
                <!--Inst icons in separate component-->
                <inst-icons :instAT="inst.asset_type"></inst-icons>
                {{inst.instrument_name}} Details
                <!--Blue cloud to indicate: cloud data is available-->
                <i
                  :id="`${inst.reference_designator}--cloud-info-available`"
                  v-if="inst.harvest_flag === true"
                  class="fas fa-cloud fa-primary"
                >
                  <b-popover
                    :target="`${inst.reference_designator}--cloud-info-available`"
                    triggers="hover"
                    placement="bottom"
                  >
                    Cloud data available.
                  </b-popover>
                </i>
                <!--Grey cloud to indicate: no cloud data available-->
                <i
                  :id="`${inst.reference_designator}--cloud-info-unavail`"
                  v-if="inst.harvest_flag === false"
                  class="fas fa-cloud text-muted"
                >
                  <b-popover
                    :target="`${inst.reference_designator}--cloud-info-unavail`"
                    triggers="hover"
                    placement="bottom"
                    no-fade
                  >
                    Currently not in Interactive Oceans systems. <br>
                    <a
                      :href="`https://ooinet.oceanobservatories.org/data_access/?search=${inst.reference_designator}`"
                      target="_blank"
                    >Click here for official OOI System.</a>
                  </b-popover>
                </i>
                <b-badge
                  id="discontinued-badge"
                  v-if="inst.discontinued"
                  pill
                  variant="secondary"
                >Discontinued</b-badge>
              </h5>
              <!-- Row of descriptions-->
              <b-row>
                <b-col cols="12">
                  <div class="instrument-attribute">
                    <strong>Description: </strong>
                    {{inst.description}}
                  </div>
                </b-col>
                <!--Status indicator text using nereus information-->
                <b-col cols="12">
                  <div class="instrument-attribute">
                    <strong>Status: </strong>
                    <status-text :instRD="inst.reference_designator"></status-text>
                  </div>
                </b-col>
                <b-col cols="12">
                  <div class="instrument-attribute">
                    <strong>Reference Designator: </strong>
                    {{inst.reference_designator}}
                  </div>
                </b-col>
                <b-col cols="6">
                  <div class="instrument-attribute">
                    <strong>Latitude: </strong>
                    {{inst.lat}}
                  </div>
                </b-col>
                <b-col cols="6">
                  <div class="instrument-attribute">
                    <strong>Longitude: </strong>
                    {{inst.lon}}
                  </div>
                </b-col>
                <b-col cols="12">
                  <div class="instrument-attribute">
                    <strong>Model: </strong>
                    {{inst.model ? inst.model : "N/A"}}
                  </div>
                </b-col>
                <b-col cols="12">
                  <div class="instrument-attribute">
                    <strong>Manufacturer: </strong>
                    {{inst.manufacturer ? inst.manufacturer : "N/A"}}
                  </div>
                </b-col>
                <b-col cols="12">
                  <div class="instrument-attribute">
                    <strong>Owner: </strong>
                    {{inst.owner ? inst.owner : "N/A"}}
                  </div>
                </b-col>
              </b-row>
              <b-row class="pt-3 pb-5">
                <b-col cols="12">
                  <b-tabs content-class="mt-3" fill>
                    <b-tab>
                      <template v-slot:title>
                        Deployments
                        <b-badge pill variant="info">{{deployments.length}}</b-badge>
                        <b-spinner
                          v-if="loadingDeployments"
                          class="ml-1"
                          type="border"
                          small
                        ></b-spinner>
                      </template>
                      <p v-if="deployments.length === 0" class="text-center">
                        <strong>No deployments found.</strong>
                      </p>
                      <b-table
                        v-if="deployments.length > 0"
                        hover
                        :items="deployments"
                        small
                        fixed
                        stacked
                      >
                      </b-table>
                    </b-tab>
                    <b-tab>
                      <template v-slot:title>
                        Annotations
                        <b-badge pill variant="info">{{annotations.length}}</b-badge>
                        <b-spinner
                          v-if="loadingAnnotations"
                          class="ml-1"
                          type="border"
                          small
                        ></b-spinner>
                      </template>
                      <p v-if="annotations.length === 0" class="text-center">
                        <strong>No annotations found.</strong>
                      </p>
                      <b-table
                        v-if="annotations.length > 0"
                        hover
                        :items="annotations"
                        small
                        fixed
                        stacked
                      >
                      </b-table>
                    </b-tab>
                    <b-tab :disabled="!inst.harvest_flag">
                      <template v-slot:title>
                        Data Availability
                      </template>
                      <show-DA :refdes="inst.reference_designator"></show-DA>
                    </b-tab>
                  </b-tabs>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-container>
      </b-overlay>
    </div>
  </b-row>
</b-card>
</template>
<script>
import _ from 'lodash';
import axios from 'axios';
import { DateTime, Duration } from 'luxon';
import { mapState, mapActions } from 'vuex';
import { APIBASEURL, APIMETADATA, APIINSTRUMENTS } from '@/utils/constants';
import InfrastructureDetailsVue from './InfrastructureDetails';
import AreaMediaVue from './AreaMedia';
import StatusIndicatorVue from '../statusIndicators/StatusIndicatorDisplay';
import StatusIndicatorTextVue from '../statusIndicators/StatusIndicatorText';
import InfraIndicatorVue from '../statusIndicators/InfraStatusIndicatorDisplay';
import InfraIcons from './infraIcons';
import InstIcons from './instIcons';
import DACard from '../DACard';

export default {
  data() {
    return {
      ooiURL: `${process.env.OOIURL}`,
      detailsObj: null,
      parametersList: [],
      RTData: {},
      RTTime: {},
      RTTimeDiff: {},
      RTInterval: null,
      diffInterval: null,
      badgeColor: 'success',
      allStreams: [],
      isHidden: false,
      instrumentCard: [],
      loadPage: false,
      timeout: null,
      deployments: [],
      annotations: [],
      loadingDeployments: false,
      loadingAnnotations: false,
    };
  },
  components: {
    'infrastructure-details': InfrastructureDetailsVue,
    'area-media': AreaMediaVue,
    'status-indicator': StatusIndicatorVue,
    'status-text': StatusIndicatorTextVue,
    'infra-indicator': InfraIndicatorVue,
    'infra-icons': InfraIcons,
    'inst-icons': InstIcons,
    'show-DA': DACard,
    // 'badge-RT': BadgeRTVue,
    // 'qaqc-btn': QAQCBtnVue,
  },
  created() {
    /** Gets the parameter listing */
    this.parameterData();
    /** Gets the RT badge information */
    // this.realTimeData();
    /** Gets the streaming details for the stream icons */
    this.streamData();
  },
  beforeDestroy() {
    clearInterval(this.diffInterval);
    clearInterval(this.RTInterval);
  },
  // mounted() {
  //   /** Run every 5 seconds */
  //   this.getRTTimeDiff();
  //   /** Run every 5 seconds */
  //   this.RTInterval = setInterval(() => {
  //     this.realTimeData();
  //   }, 10000);
  //   this.diffInterval = setInterval(() => {
  //     this.getRTTimeDiff();
  //   }, 1000);
  // },
  methods: {
    ...mapActions([
      'mapSelectInfrastructure',
      'toggleSidebar',
    ]),
    /**
     * This updates the @change event on the sidebar which will
     * make the sidebar visible or hidden
     * @param {*} value - boolean
     */
    updateSidebar(value) {
      this.toggleSidebar({ showSidebar: value });
    },
    /**
     * Clears the timeout when loading is done
     */
    clearTimeout() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
    },
    /**
     * Sets the timeout for the loading and calls the
     * callback function. Timeout is 2 seconds.
     * @param {*} callback
     */
    setTimeout(callback) {
      this.clearTimeout();
      this.timeout = setTimeout(() => {
        this.clearTimeout();
        callback();
      }, 3000);
    },
    /**
     * Calls the timeout on the instrument details page
     * loading
     */
    loadingPage() {
      this.loadPage = true;
      this.setTimeout(() => {
        this.loadPage = false;
      });
    },
    /**
     * Show instrument detail modal
     * @param {object} item - object of instrument data
     */
    showDetailsModal(refdes) {
      this.loadingPage();
      axios
        .get(`${APIBASEURL}/${APIMETADATA}/${APIINSTRUMENTS}`, {
          params: {
            refdes,
          },
        })
        .then((resp) => {
          // Data for instrument card details
          this.instrumentCard = resp.data;
        });
      this.fetchDeployments(refdes);
      this.fetchAnnotations(refdes);
      this.isHidden = true;
    },
    /** This method calls the deployment endpoint with params: reference
     * designator.
     */
    fetchDeployments(refdes) {
      this.loadingDeployments = true;
      const params = {
        refdes,
      };
      axios
        .get(`${APIBASEURL}/${APIMETADATA}/get_deployments`, { params })
        .then((resp) => {
          this.processDeployments(resp.data);
          this.loadingDeployments = false;
        });
    },
    /** This method processes the date from the deployment end point for a
     * specific reference designator. The table will display the deployments
     * in order of most recent to oldest. The table headings will be (from
     * left to right): deployment number, UID, start date, end date, location
     * (latitude and longitude).
     */
    processDeployments(deployments) {
      const sortedDeployments = _.reverse(
        _.sortBy(deployments, ['deployment_number']),
      );
      this.deployments = sortedDeployments.map((dep) => {
        const newDep = {
          number: dep.deployment_number,
          UID: dep.uid,
          start_date: DateTime.fromMillis(dep.deployment_start)
            .toUTC()
            .toLocaleString(DateTime.DATETIME_FULL),
          end_date:
            dep.deployment_end !== null
              ? DateTime.fromMillis(dep.deployment_end)
                .toUTC()
                .toLocaleString(DateTime.DATETIME_FULL)
              : 'Ongoing',
          location: `(${dep.lat}, ${dep.lon})`,
        };
        return newDep;
      });
    },
    /** This method calls the annotation end point with params: reference
     * designator, start date and end date.
     */
    fetchAnnotations(refdes) {
      this.loadingAnnotations = true;
      const params = {
        ref: refdes,
        start_dt: DateTime.utc(2014, 1, 1, 0, 0, 0).toFormat('yyyyLLdd'),
        end_dt: DateTime.utc().toFormat('yyyyLLdd'),
      };
      axios
        .get(`${APIBASEURL}/${APIMETADATA}/get_annotations`, { params })
        .then((resp) => {
          this.processAnnotations(resp.data.annotations[refdes]);
          this.loadingAnnotations = false;
        });
    },
    /** This method processes the date from the annotation end point for a
     * specific reference designator. The table will display the annotations
     * in order of most recent to oldest. The annotation table will display the
     * annotation ID, reference designator, start date, end date, comment,
     * author, parameters, QC (quality control) code, and exclusion flag.
     */
    processAnnotations(annotationData) {
      this.annotations = annotationData.map((anno) => {
        const newRefList = [
          anno.site_rd,
          anno.infrastructure_rd,
          anno.instrument_rd,
          anno.stream_method,
          anno.stream_rd,
        ];
        const newAnno = {
          annotation_ID: anno.id,
          reference: _.join(
            _.remove(newRefList, (r) => r !== null),
            '-',
          ),
          start:
            anno.begin_date !== null
              ? DateTime.fromMillis(anno.begin_date)
                .toUTC()
                .toLocaleString(DateTime.DATE_FULL)
              : 'N/A',
          end:
            anno.end_date !== null
              ? DateTime.fromMillis(anno.end_date)
                .toUTC()
                .toLocaleString(DateTime.DATE_FULL)
              : 'N/A',
          comment: anno.annotation,
          author: anno.source,
          parameters:
            anno.parameters.length > 0 ? _.join(anno.parameters, ', ') : 'N/A',
          QC_flag: anno.qc_flag !== null ? _.startCase(anno.qc_flag) : 'N/A',
          exclude: anno.exclusion_flag ? 'Yes' : 'No',
        };
        return newAnno;
      });
    },
    /**
     * Gets latest deployment object from gets_deployment endpoint
     * @param {string} refDes - instrument reference designator
     */
    instDetails(refDes) {
      const params = {
        refdes: refDes,
      };
      axios
        .get(`${APIBASEURL}/${APIMETADATA}/get_deployments`, { params })
        .then((resp) => {
          this.determineDetails(resp.data);
        });
    },
    /** REALTIME DATA */
    /**
     * Axios call to get the RT data
     * @param {*} instRD - instrument reference designator
     */
    getLatestData(instRD) {
      const params = {
        ref: instRD,
      };
      return axios.get(`${APIBASEURL}/feed/`, { params });
    },
    /**
     * Initial created() function to go through the instrumentList and
     * do the Axios get call for each instrument.
     *
     * Calls the async function to do await for the array of data.
     */
    realTimeData() {
      const promises = [];
      const instruments = [];
      this.instrumentsList.forEach((val) => {
        const promise = this.getLatestData(val.reference_designator);
        promises.push(promise);
        instruments.push(val);
      });
      this.getAllData(promises, instruments);
    },
    /**
     * This async function waits until all promises are done.
     * For each set of results, calls setRTData to set
     * the global RTData variable.
     * @param {Array} promises - array of promise calls
     * @param {Array} instruments - list of instruments
     */
    async getAllData(promises, instruments) {
      // TODO: add loader
      const results = await Promise.all(promises);
      _.forEach(results, (resp, idx) => {
        this.setRTData(resp.data, instruments[idx].reference_designator);
        this.setRTTime(resp.data, instruments[idx]);
      });
    },
    /**
     * Assigns the global RTData variable to the data passed in
     * @param {object} data - object of data from the RT results
     * @param {string} instRD - instrument reference designator
     */
    setRTData(data, instRD) {
      const newData = _.defaultsDeep({}, this.RTData);
      newData[instRD] = data;
      this.RTData = newData;
    },
    /**
     * Get time of the realtime
     * @param {object} data - object of data from RT results
     * @param {object} inst - object of instrument data
     */
    setRTTime(data, inst) {
      let time = null;
      if (_.has(data, inst.reference_designator)) {
        const instData = data[inst.reference_designator];
        const streamKey = `${inst.preferred_stream_method}-${inst.preferred_stream}`;
        if (_.has(instData, streamKey)) {
          const streamData = instData[streamKey];
          if (_.has(streamData, 'time')) {
            time = streamData.time;
          }
        }
      }
      const newTime = _.defaultsDeep({}, this.RTTime);
      newTime[inst.reference_designator] = time;
      this.RTTime = newTime;
    },
    /**
     * Get time difference for realtime data
     */
    getRTTimeDiff() {
      const now = DateTime.utc();
      const timeDiffs = _.defaultsDeep({}, this.RTTimeDiff);
      this.instrumentsList.forEach((inst) => {
        const lastTime = this.RTTime[inst.reference_designator];
        if (lastTime) {
          const lastTimeDT = DateTime.fromISO(lastTime, { zone: 'utc' });
          const timeDiff = now.diff(lastTimeDT, [
            'months',
            'days',
            'hours',
            'minutes',
            'seconds',
          ]);
          const filtered = {};
          _.forEach(timeDiff.toObject(), (val, key) => {
            if (val !== 0) {
              filtered[key] = _.floor(val);
            }
          });
          const cleanedDiff = Duration.fromObject(filtered);
          timeDiffs[inst.reference_designator] = `${cleanedDiff.toHuman({ listStyle: 'long' })} ago.`;
        } else {
          timeDiffs[inst.reference_designator] = null;
        }
      });
      this.RTTimeDiff = timeDiffs;
    },
    /** PARAMETER LISTING */
    /**
     * Initial created() function that goes through the instrumentList and
     * does the Axios get call for each instrument.
     *
     * Calls the async function to await for the array of data.
     */
    parameterData() {
      const promises = [];
      this.instrumentsList.forEach((value) => {
        const promise = this.getParameterData(value.reference_designator);
        promises.push(promise);
      });
      this.getAllParameterData(promises);
    },
    /**
     * Axios call to get the parameter data information
     * @param {string} instRD - instrument reference designator
     */
    getParameterData(instRD) {
      const params = {
        include_params: true,
        refdes: instRD,
      };
      return axios.get(`${APIBASEURL}/${APIMETADATA}/${APIINSTRUMENTS}`, { params });
    },
    /**
     * This async function waits until all promises are finished.
     *  It then pushes the data into a temp array and then assigns
     * the global variable to the temp variable.
     * @param {Array} promises - array of promise calls
     */
    async getAllParameterData(promises) {
      const results = await Promise.all(promises);
      const paramsList = [];
      _.forEach(results, (resp) => {
        paramsList.push(resp.data[0]);
      });
      this.parametersList = paramsList;
    },
    /**
     * Extracts parameters from instruments using the /metadata/instrument endpoint.
     * @param {string} refDes - instrument reference designator
     */
    extractParameters(refDes) {
      /** Filters the parametersList for a specific instrument reference designator */
      const filteredStreams = _.filter(this.parametersList, [
        'reference_designator',
        refDes,
      ]);
      let dataProducts = [];
      /** Maps all the dataProducts for specific instruments  */
      _.map(filteredStreams, (val) => {
        if (_.isEmpty(val.products)) {
          dataProducts = null;
        } else {
          dataProducts = val.products;
        }
      });
      /** Returns null if an instrument has no data */
      if (_.isNull(dataProducts)) {
        return null;
      }
      /** Return the unique data products and filters out time from the listing */
      return this.filterProducts(dataProducts);
    },
    /**
     * Filters the extracted parameter listing to remove duplicates and time
     * @param {array} mappedProducts - array of unique data products
     */
    filterProducts(mappedProducts) {
      const filteredParams = _.uniqBy(
        _.filter(
          _.flatMap(mappedProducts),
          (dataProduct) => dataProduct.reference_designator !== 'time',
        ),
        'parameter_name',
      );
      /** Checks if it's an empty array once time is removed */
      if (_.isEmpty(filteredParams)) {
        return null;
      }
      return filteredParams;
    },
    /** STREAMING ICONS */
    /**
     * Initial created() function that goes through the instrumentList and
     * does the Axios get call for each instrument.
     *
     * Calls the async function to await for the array of data.
     */
    streamData() {
      const promises = [];
      this.instrumentsList.forEach((value) => {
        const promise = this.getStreamDetails(value.reference_designator);
        promises.push(promise);
      });
      this.getAllStreamingData(promises);
    },
    /**
     * Gets latest stream information from /instrument/streams endpoint.
     * @param {string} instRD - instrument reference designator
     */
    getStreamDetails(instRD) {
      return axios.get(`${APIBASEURL}/${APIMETADATA}/instrument/${instRD}/streams`);
    },
    /**
     * This async function waits until all promises are finished.
     * Then it pushes the data into a temp array and assigns
     * the global variable to the temp variable.
     * @param {Array} promises - array of promise calls
     */
    async getAllStreamingData(promises) {
      const results = await Promise.all(promises);
      const streamsList = [];
      _.forEach(results, (resp) => {
        streamsList.push(resp.data[0]);
      });
      this.allStreams = streamsList;
    },
    /**
     * Returns whether a specific instrument has active streams or not in the IO system.
     * @param {string} refDes - instrument reference designator
     */
    isActiveStreams(refDes) {
      const activeStreams = _.filter(this.allStreams, ['reference_designator', refDes]);
      if (activeStreams.length > 0) {
        return true;
      }
      return false;
    },
    /** DETAILS MODAL */
    /**
     * Filters by the latest deployment number for the latest object.
     * @param {object} data - object of instrument data
     */
    determineDetails(data) {
      const latestDeployment = Math.max(
        ...data.map((o) => o.deployment_number),
      );
      const getDeploymentsObj = _.find(data, [
        'deployment_number',
        latestDeployment,
      ]);
      const getInstObj = _.find(this.instruments, [
        'reference_designator',
        getDeploymentsObj.reference_designator,
      ]);
      this.compareInstAndGetDeployments(getDeploymentsObj, getInstObj);
    },
    /**
     * This method fills in any missing information on instruments with
     * information from get_deployments endpoint.
     * @param {object} deploymentsObj - object of deployment data
     * @param {object} instObj - object of instrument data
     */
    compareInstAndGetDeployments(deploymentsObj, instObj) {
      /**
       * Holder variable for the instrument object - used to compare data
       * from get_deployments
       */
      const test = instObj;
      /** Gets Instrument Details description */
      if (instObj.description === null && deploymentsObj.description === null) {
        test.description = 'No description available.';
      } else if (instObj.description === null) {
        test.description = deploymentsObj.description;
      }
      /** Gets Instrument Details latitude */
      if (instObj.lat === null) {
        test.lat = deploymentsObj.lat;
      } else {
        test.lat = instObj.lat;
      }
      /** Gets Instrument Details longitude */
      if (instObj.lon === null) {
        test.lon = deploymentsObj.lon;
      } else {
        test.lon = instObj.lon;
      }
      /** Gets Instrument Details owner */
      if (instObj.owner === null && deploymentsObj.owner === null) {
        test.owner = 'N/A';
      } else if (instObj.owner === null) {
        test.owner = deploymentsObj.owner;
      }
      /** Gets Instrument Details model */
      if (instObj.model === null) {
        test.model = 'N/A';
      } else {
        test.model = instObj.model;
      }
      /** Gets Instrument Details manufacturer */
      if (instObj.manufacturer === null) {
        test.manufacturer = deploymentsObj.manufacturer;
      } else {
        test.manufacturer = instObj.manufacturer;
      }
      this.detailsObj = test;
    },
    /** Old function to extract parameters from old instrument catalog endpoint */
    // extractParameters(refDes) {
    //   // extract parameters for instruments that have been harvested.
    //   const filteredStreams = _.filter(this.instrumentsCatalog, [
    //     'instrument_rd',
    //     refDes,
    //   ]);
    //   // console.log(filteredStreams);
    //   const paramList = filteredStreams.map((st) => st.parameters);
    //   // console.log('PARAM')
    //   // console.log(paramList)
    //   return _.uniqBy(
    //     _.filter(
    //       _.flatMap(paramList),
    //       (o) => o.reference_designator !== 'time',
    //     ),
    //     'parameter_name',
    //   );
    // },
  },
  computed: {
    ...mapState({
      mapSelectedInfra: (state) => state.mapSelectedInfra,
      instruments: (state) => state.instruments,
      sites: (state) => state.sites,
      statusIndicators: (state) => state.statusIndicators,
      wpContentSelectedInfra: (state) => state.wpContentSelectedInfra,
      instrumentDetailModals: (state) => state.instrumentDetailModals,
      showSidebar: (state) => state.showSidebar,
      sidebarContent: (state) => state.sidebarContent,
    }),
    /** Get infrastrucuture details */
    infraDetails() {
      return this.mapSelectedInfra.properties;
    },
    /** Get site details */
    siteDetails() {
      const refDes = this.mapSelectedInfra.properties.reference_designator;
      const siteRefDes = refDes.slice(0, 8);
      const site = _.find(this.sites.features, [
        'properties.reference_designator',
        siteRefDes,
      ]);
      return site;
    },
    /** Get listing of instruments */
    instrumentsList() {
      const insts = _.filter(this.instruments, (o) => {
        const infra = o.reference_designator.slice(0, 14);
        return infra === this.infraDetails.reference_designator;
      });
      return _.sortBy(insts, ['instrument_name']);
    },
  },
};
</script>
<style>
/** */
#discontinued-badge {
  vertical-align: text-top;
  font-size: 65%;
}
.b-sidebar > .b-sidebar-body  {
  overflow-y: hidden;
}
.map-detail-container {
  width: 35%;
  height: 100%;
  z-index: 999;
  position: absolute;
  right: 0;
  padding: 1rem;
}
.map-detail-title {
  height: 5%;
  font-size: 1.2rem;
}

.instrument-title {
  font-size: 1rem;
}

.map-detail-card {
  width: 100%;
  height: 100%;
}

.map-detail-card-body {
  width: 100%;
  height: 100%;
}

.map-detail-nav {
  width: 100%;
  height: 5%;
}

.map-detail-content {
  height: 88%;
  width: 100%;
  padding-top: 1rem;
}

.map-detail-instruments-list {
  width: 100%;
  height: 100%;
  overflow-y: scroll;
}

.fa-primary {
  color: #3995A3 !important;
}
.fa-success {
  color: #28a745 !important;
}
.fa-danger {
  color: #dc3545 !important;
}
.instrument-attribute {
  background-color: rgba(211, 211, 211, 0.3);
  border-radius: 0.5rem;
  margin: 0.3rem 0 0.3rem 0;
  padding: 0.7rem;
}
</style>
